<template>
    <div class="im">
        <div class="im-layout">
            <!-- Left: Editor -->
            <div class="im-editor" v-if="!hideForm">
                <label class="im-editor__label">Personalizeaza mesajul</label>
                <textarea
                    v-model="userProjectMessage"
                    @keyup="handleCharCount"
                    class="im-editor__textarea"
                    :placeholder="demoProjectMessage"
                    rows="4"
                ></textarea>
                <div class="im-editor__footer">
                    <span
                        class="im-char"
                        :class="{
                            'im-char--danger': messageCharCount <= 0,
                            'im-char--warn': messageCharCount > 0 && messageCharCount < 10,
                            'im-char--ok': messageCharCount >= 10,
                        }"
                    >{{ messageCharCount >= 0 ? messageCharCount : 0 }} caractere ramase</span>
                </div>
                <button class="im-save" @click="saveProjectMessage()">
                    <font-awesome-icon icon="fa-solid fa-check" />
                    <span>Salveaza mesajul</span>
                </button>
            </div>

            <!-- Right: Preview mockup -->
            <div class="im-mockup">
                <div class="im-mockup__label">Previzualizare {{ isEmailScope ? 'Email' : (isWhatsAppScope ? 'WhatsApp' : 'SMS') }}</div>

                <!-- ── SMS / WhatsApp: Phone frame ── -->
                <div v-if="!isEmailScope" class="im-phone" :class="{ 'im-phone--whatsapp': isWhatsAppScope }">
                    <div class="im-phone__notch"></div>
                    <div class="im-phone__status">
                        <span>9:41</span>
                        <span class="im-phone__status-icons">
                            <span>●●●●</span>
                        </span>
                    </div>
                    <div class="im-phone__header" :class="{ 'im-phone__header--whatsapp': isWhatsAppScope }">
                        <span class="im-phone__sender">{{ isWhatsAppScope ? 'Invitat' : 'InvitatiiSmart' }}</span>
                    </div>
                    <div class="im-phone__body">
                        <div class="im-phone__bubble" :class="{ 'im-phone__bubble--whatsapp': isWhatsAppScope }">
                            <span>{{ userProjectMessage ? userProjectMessage : demoProjectMessage }}</span>
                            <a href="#" class="im-phone__link" @click.prevent>{{ demoMessageURL }}</a>
                        </div>
                        <div class="im-phone__time">Acum</div>
                    </div>
                </div>

                <!-- ── Email: Template preview ── -->
                <div v-else class="im-email">
                    <!-- Inbox bar -->
                    <div class="im-email__toolbar">
                        <div class="im-email__dots">
                            <span></span><span></span><span></span>
                        </div>
                        <div class="im-email__tab">Inbox</div>
                    </div>
                    <!-- Sender row -->
                    <div class="im-email__sender-row">
                        <div class="im-email__avatar">
                            <font-awesome-icon icon="fa-solid fa-envelope" />
                        </div>
                        <div class="im-email__sender-meta">
                            <div class="im-email__from">InvitatiiSmart</div>
                            <div class="im-email__subject">Ești invitat la {{ projectName }}!</div>
                            <div class="im-email__to">Către: invitat@email.com</div>
                        </div>
                    </div>
                    <!-- Email body preview -->
                    <div class="im-email__body">
                        <div class="im-email__heading">Ești invitat!</div>
                        <div class="im-email__subheading">Ai primit o invitație personalizată</div>

                        <div class="im-email__greeting">Bună, <strong>Nume Invitat</strong>,</div>

                        <div class="im-email__msg">{{ userProjectMessage ? userProjectMessage : demoProjectMessage }}</div>

                        <!-- Event card -->
                        <div class="im-email__event-card">
                            <div class="im-email__event-label">Eveniment</div>
                            <div class="im-email__event-value">{{ projectName }}</div>
                            <div class="im-email__event-label" style="margin-top:6px;">Data</div>
                            <div class="im-email__event-value">{{ formattedProjectDate }}</div>
                        </div>

                        <!-- CTA -->
                        <div class="im-email__cta-wrap">
                            <span class="im-email__cta">Vizualizează Invitația</span>
                        </div>

                        <div class="im-email__footer-text">Te rugăm să confirmi participarea accesând butonul de mai sus.</div>
                    </div>
                    <!-- Footer -->
                    <div class="im-email__footer">
                        <span>&copy; {{ new Date().getFullYear() }} InvitatiiSmart</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { validateMessageContent } from '@/utils/contentSanitizer';

export default {
    name: 'InvitationMessage',
    components: {},
    props: {
        projectId: {
            required: true,
        },
        projectName: {
            required: true,
        },
        projectSlug: {
            required: true,
        },
        hideForm: {
            type: Boolean,
        },
        userMessage: {
            type: String,
            required: true,
        },
        nameInURL: {
            type: Number,
            default: 0,
        },
        template_type: {
            type: String,
            default: 'simple',
        },
        isEmailScope: {
            type: Boolean,
            default: false,
        },
        isWhatsAppScope: {
            type: Boolean,
            default: false,
        },
        projectDate: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            currentProjectId: null,
            messageCharCountLimit: this.isEmailScope ? 300 : (this.isWhatsAppScope ? 200 : 150),
            messageCharCount: this.isEmailScope ? 300 : (this.isWhatsAppScope ? 200 : 150),
            userProjectMessage: '',
            demoProjectMessage:
                'Salutare! Ai primit o invitatie digitala. O poti accesa aici:',
            demoMessageURL: this.generateDemoMessageURL(),
        };
    },
    created() {
        this.handleCharCount();
        this.userProjectMessage = this.userMessage;
    },
    setup() {
        // Get toast interface
        const toast = useToast();

        // Make it available inside methods
        return { toast };
    },
    computed: {
        formattedProjectDate() {
            if (!this.projectDate) return '—';
            const parts = this.projectDate.split('-');
            if (parts.length < 3) return this.projectDate;
            return `${parts[2]}.${parts[1]}.${parts[0]}`;
        },
    },
    watch: {
        // Watch for changes in isEmailScope to adjust character limits
        isEmailScope(newVal) {
            this.messageCharCountLimit = newVal ? 300 : 150;
            this.handleCharCount(); // Recalculate remaining characters
        },
        projectId: 'updateDemoMessageURL',
        projectSlug: 'updateDemoMessageURL',
        nameInURL: 'updateDemoMessageURL',
        template_type: 'updateDemoMessageURL',
    },
    methods: {
        // Method to generate the URL dynamically
        generateDemoMessageURL() {
            const baseUrl = 'https://invitatiismart.ro/#';
            const safeSlug = (this.projectSlug || '').toLowerCase().trim().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '');
            if (this.template_type === 'simple') {
                // Simple project URL logic
                return `${baseUrl}/ps/${this.projectId}${
                    this.nameInURL == 1
                        ? '/' + safeSlug
                        : ''
                }`;
            } else {
                // Complex project URL logic
                return `${baseUrl}/public/${this.projectId}/invitationId${
                    this.nameInURL == 1
                        ? '/' + safeSlug
                        : ''
                }`;
            }
        },
        // Method to update the demoMessageURL when relevant props change
        updateDemoMessageURL() {
            this.demoMessageURL = this.generateDemoMessageURL();
        },
        async saveProjectMessage() {
            if (!this.userProjectMessage) {
                this.toast.info(`Mesajul e gol! Nu s-au efectual actualizari.`);
                return;
            }

            // Validate content - block URLs
            const validation = validateMessageContent(this.userProjectMessage);
            if (!validation.valid) {
                this.toast.error(validation.error);
                return;
            }
            try {
                // Retrieve user_id from localStorage
                const jwtToken = localStorage.getItem('userToken');

                const formData = {
                    projectMessage: this.userProjectMessage,
                    projectID: this.projectId,
                    scope: this.isWhatsAppScope ? 'whatsapp' : (this.isEmailScope ? 'email' : 'sms'),
                };

                await axios
                    .post(
                        '/api/index.php?endpoint=saveProjectMessage',
                        formData, // Pass form data in the request payload
                        {
                            headers: {
                                'Content-Type':
                                    'application/x-www-form-urlencoded',
                                Authorization: `Bearer ${jwtToken}`,
                            },
                        }
                    )
                    .then((result) => {
                        if (result.data.success) {
                            this.toast.success(
                                `Mesajul a fost salvat cu succes!`
                            );

                            if (this.isWhatsAppScope) {
                                this.$store.commit(
                                    'setProjectInvitationMessageWhatsApp',
                                    this.userProjectMessage
                                );
                            } else if (this.isEmailScope) {
                                this.$store.commit(
                                    'setProjectInvitationMessageEmail',
                                    this.userProjectMessage
                                );
                            } else {
                                this.$store.commit(
                                    'setProjectInvitationMessage',
                                    this.userProjectMessage
                                );
                            }
                        } else {
                            this.toast.error(
                                `A aparut o eroare. ${result.data.message}`
                            );
                        }
                    })
                    .catch((error) => {
                        this.toast.error(`A aparut o eroare. ${error}`);
                    });
            } catch (error) {
                console.error(
                    'Error saving message:',
                    error.response ? error.response.data : error.message
                );
            }
        },
        handleCharCount() {
            this.messageCharCount =
                this.messageCharCountLimit -
                (this.userProjectMessage
                    ? this.userProjectMessage.length
                    : this.demoProjectMessage.length) -
                this.demoMessageURL.length;

            // Check if the character count exceeds the limit
            if (this.messageCharCount < 0) {
                // Trim the text to the maximum allowed characters
                this.userProjectMessage = this.userProjectMessage.substring(
                    0,
                    this.messageCharCountLimit - this.demoMessageURL.length
                );
            }
        },
    },
};
</script>

<style scoped>
.im { width: 100%; }

/* ── Layout: side-by-side ── */
.im-layout {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 28px;
    align-items: start;
}

/* ── Editor (left) ── */
.im-editor {
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.im-editor__label {
    font-size: 13px;
    font-weight: 700;
    color: #334155;
    text-align: left;
}
.im-editor__textarea {
    width: 100%;
    padding: 12px 14px;
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    font-size: 14px;
    color: #0f172a;
    background: #fff;
    outline: none;
    resize: vertical;
    min-height: 100px;
    transition: border-color 0.15s, box-shadow 0.15s;
    box-sizing: border-box;
    font-family: inherit;
}
.im-editor__textarea:focus {
    border-color: #7c3aed;
    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.08);
}
.im-editor__textarea::placeholder { color: #94a3b8; }
.im-editor__footer {
    display: flex;
    justify-content: flex-end;
}

/* Char badge */
.im-char {
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 6px;
}
.im-char--ok { background: #dcfce7; color: #16a34a; }
.im-char--warn { background: #fef3c7; color: #d97706; }
.im-char--danger { background: #fee2e2; color: #dc2626; }

/* Save */
.im-save {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 9px 20px;
    border-radius: 8px;
    border: none;
    background: #0f172a;
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;
    align-self: flex-start;
    margin-top: 4px;
}
.im-save:hover { background: #1e293b; }

/* ── Mockup (right) ── */
.im-mockup {
    display: flex;
    flex-direction: column;
    align-items: center;
}
.im-mockup__label {
    font-size: 11px;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 12px;
}

/* ── Phone frame (SMS) ── */
.im-phone {
    width: 280px;
    min-height: 380px;
    background: #0f172a;
    border-radius: 36px;
    padding: 12px;
    box-shadow: 0 20px 60px rgba(0,0,0,0.18), inset 0 0 0 2px #334155;
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: hidden;
}
.im-phone__notch {
    width: 100px;
    height: 22px;
    background: #0f172a;
    border-radius: 0 0 16px 16px;
    margin: 0 auto;
    position: relative;
    z-index: 2;
}
.im-phone__status {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 16px 6px;
    font-size: 11px;
    font-weight: 600;
    color: #fff;
}
.im-phone__status-icons { font-size: 8px; letter-spacing: 2px; }
.im-phone__header {
    background: #1e293b;
    padding: 10px 16px;
    border-radius: 16px 16px 0 0;
    text-align: center;
}
.im-phone__sender {
    font-size: 13px;
    font-weight: 700;
    color: #fff;
}
.im-phone__body {
    flex: 1;
    background: #f8fafc;
    border-radius: 0 0 16px 16px;
    padding: 16px 12px;
    display: flex;
    flex-direction: column;
}
.im-phone__bubble {
    background: #e2e8f0;
    border-radius: 16px 16px 16px 4px;
    padding: 10px 14px;
    font-size: 13px;
    color: #0f172a;
    line-height: 1.5;
    word-break: break-word;
    max-width: 90%;
}
.im-phone--whatsapp { background: #111b21; }
.im-phone--whatsapp .im-phone__body { background: #0b141a; }
.im-phone__header--whatsapp { background: #1f2c34; border-color: #1f2c34; }
.im-phone__header--whatsapp .im-phone__sender { color: #e9edef; }
.im-phone__bubble--whatsapp {
    background: #005c4b;
    color: #e9edef;
    border-radius: 8px 8px 4px 8px;
    align-self: flex-end;
}
.im-phone__bubble--whatsapp .im-phone__link { color: #53bdeb; }
.im-phone__link {
    display: block;
    margin-top: 4px;
    color: #2563eb;
    text-decoration: underline;
    font-size: 12px;
    word-break: break-all;
}
.im-phone__time {
    font-size: 10px;
    color: #94a3b8;
    margin-top: 6px;
    padding-left: 4px;
}

/* ── Email template preview ── */
.im-email {
    width: 100%;
    max-width: 380px;
    background: #f4f4f7;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 8px 30px rgba(0,0,0,0.08);
    overflow: hidden;
}
.im-email__toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    background: #f1f5f9;
    border-bottom: 1px solid #e2e8f0;
}
.im-email__dots {
    display: flex;
    gap: 5px;
}
.im-email__dots span {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #cbd5e1;
}
.im-email__dots span:first-child { background: #f87171; }
.im-email__dots span:nth-child(2) { background: #fbbf24; }
.im-email__dots span:last-child { background: #34d399; }
.im-email__tab {
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}
/* Sender row */
.im-email__sender-row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 14px;
    background: #fff;
    border-bottom: 1px solid #f0f0f5;
}
.im-email__avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #ede9fe;
    color: #7c3aed;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    flex-shrink: 0;
}
.im-email__sender-meta { text-align: left; min-width: 0; }
.im-email__from {
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
}
.im-email__subject {
    font-size: 11px;
    font-weight: 600;
    color: #475569;
    margin-top: 1px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.im-email__to {
    font-size: 10px;
    color: #94a3b8;
    margin-top: 1px;
}
/* Email body – mimics actual email layout */
.im-email__body {
    margin: 12px 12px 0;
    padding: 20px 18px 16px;
    background: #fff;
    border-radius: 10px 10px 0 0;
    font-size: 12px;
    color: #374151;
    line-height: 1.6;
    word-break: break-word;
    text-align: left;
}
.im-email__heading {
    font-size: 16px;
    font-weight: 700;
    color: #1f2937;
    text-align: center;
    margin-bottom: 2px;
}
.im-email__subheading {
    font-size: 10px;
    color: #9ca3af;
    text-align: center;
    margin-bottom: 14px;
}
.im-email__greeting {
    font-size: 12px;
    color: #374151;
    margin-bottom: 8px;
}
.im-email__msg {
    font-size: 12px;
    color: #374151;
    line-height: 1.6;
    margin-bottom: 14px;
}
.im-email__event-card {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 10px 14px;
    margin-bottom: 14px;
}
.im-email__event-label {
    font-size: 9px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #9ca3af;
    font-weight: 600;
}
.im-email__event-value {
    font-size: 12px;
    font-weight: 600;
    color: #1f2937;
    margin-top: 1px;
}
.im-email__cta-wrap {
    text-align: center;
    margin-bottom: 14px;
}
.im-email__cta {
    display: inline-block;
    padding: 8px 24px;
    background: #6366f1;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    border-radius: 6px;
    letter-spacing: 0.2px;
}
.im-email__footer-text {
    font-size: 10px;
    color: #6b7280;
    text-align: center;
    line-height: 1.4;
}
/* Footer */
.im-email__footer {
    margin: 0 12px 12px;
    padding: 10px 18px;
    background: #f9fafb;
    border-radius: 0 0 10px 10px;
    border-top: 1px solid #f0f0f5;
    text-align: center;
    font-size: 9px;
    color: #9ca3af;
}

/* ── Responsive ── */
@media (max-width: 640px) {
    .im-layout {
        grid-template-columns: 1fr;
        gap: 20px;
    }
    .im-mockup { order: -1; }
    .im-phone { width: 260px; min-height: 340px; }
    .im-email { max-width: 100%; }
}
</style>
